import Point from 'ol/geom/Point';
import { Style, Text, Fill, Stroke } from 'ol/style';

import { fonts, toCssFontSize } from 'cadenza/utils/fonts';
import { resolveReferenceToIndex } from 'cadenza/utils/json';
import { assert } from 'cadenza/utils/custom-error';

const BACKGROUND_STYLE_HALO = 'halo';
const BACKGROUND_STYLE_HALO_WIDTH = 3; // No const found in backend; This fits best.

interface AnchorDictionary {
  [index: string]: {
    textAlign: string;
    textBaseline: string;
  };
}

const ANCHOR_TEXT_PROPERTIES: AnchorDictionary = Object.freeze({
  'Center': { textAlign: 'center', textBaseline: 'middle' },
  'North': { textAlign: 'center', textBaseline: 'top' },
  'Northeast': { textAlign: 'right', textBaseline: 'top' },
  'East': { textAlign: 'right', textBaseline: 'middle' },
  'Southeast': { textAlign: 'right', textBaseline: 'bottom' },
  'South': { textAlign: 'center', textBaseline: 'bottom' },
  'Southwest': { textAlign: 'left', textBaseline: 'bottom' },
  'West': { textAlign: 'left', textBaseline: 'middle' },
  'Northwest': { textAlign: 'left', textBaseline: 'top' }
});

interface ResolveOptions {
  attributes: Attributes;
  labelStyleReference: string;
  position: number[];
  rotation: number;
  zIndex: number;
}

interface Attributes {
  [index: string]: string;
}

interface LabelStyleSourceData {
  attributeStyles: AttributeStyleSourceData[];
  backgroundColor: string;
  backgroundStyle: string;
  foregroundColor: string;
  anchor: string;
}

interface AttributeStyleSourceData {
  font: FontSourceData;
  name: string;
}

interface FontSourceData {
  fontFamily: string;
  fontSize: number;
  fontStyle: string;
  fontWeight: string;
  textDecorationColor: string;
  textDecorationLine: string;
  textDecorationThickness: string;
}

interface AttributeStyle {
  font: string;
  name: string;
}

const FALLBACK_FONT_FAMILY = 'Arial';
const LINE_HEIGHT_SCALE_FACTOR = 1.2;

export class FeatureLabelStyleResolver {

  /**
   * Cache for OpenLayer Style objects.
   *
   * @private
   */
  _labelStyles: LabelStyleSourceData[];
  /**
   * Each label style can have multiple attributes
   * and each attribute can have a font setting.
   * We cache the font settings according to the label style index
   * and the attribute name.
   *
   * @example
   * _labelAttributeStyle[labelStyleIndex][attributeName];
   * _labelAttributeStyle[0]['FLAECHE'];
   */
  _labelAttributeStyles: AttributeStyle[][];

  /**
   * Style resolver: creates OpenLayer styles for each feature label
   *
   * @param labelStyles - Raw label styles.
   */
  constructor (labelStyles: LabelStyleSourceData[] = []) {
    this._labelStyles = labelStyles;
    this._labelAttributeStyles = convertAttributeStyles(labelStyles);
  }

  /**
   * Provides a style according to the given style reference and the geometryTyp.
   *
   * @see {@link https://openlayers.org/en/latest/apidoc/module-ol_style_Style.html#~StyleFunction}
   * @param options
   * @param options.labelStyleReference - A JSON Reference to the style.
   * @param options.attributes - The attributes of a feature.
   * @param options.position - The label position
   * @param options.rotation - The label rotation
   * @param options.zIndex - zIndex
   * @return Label as openlayers style.
   */
  resolve ({
    labelStyleReference,
    attributes,
    position,
    rotation,
    zIndex
  }: ResolveOptions): Style {
    const styleIndex = resolveReferenceToIndex(labelStyleReference);
    const labelStyleConfig = this._labelStyles[styleIndex];
    const labelAttributeStyle = this._labelAttributeStyles[styleIndex];

    assert(labelStyleConfig, 'Label style is not available.');
    assert(labelAttributeStyle, 'Label attribute style is not available.');

    const olText: string[] = [];
    labelAttributeStyle.forEach((attributeStyle: AttributeStyle, index: number) => {
      olText.push(attributes[attributeStyle.name], attributeStyle.font);
      if (index !== labelAttributeStyle.length - 1) {
        olText.push('\n', '');
      }
    });

    const { textAlign, textBaseline } = ANCHOR_TEXT_PROPERTIES[labelStyleConfig.anchor];

    const labelStyle = new Style({
      geometry: new Point(position),
      text: new Text({
        text: olText,
        textAlign,
        textBaseline,
        rotation: -rotation,
        fill: new Fill({
          color: labelStyleConfig.foregroundColor
        }),
        stroke: isHalo(labelStyleConfig.backgroundStyle)
          ? new Stroke({
            color: labelStyleConfig.backgroundColor,
            width: BACKGROUND_STYLE_HALO_WIDTH
          })
          : undefined,
        backgroundFill: !isHalo(labelStyleConfig.backgroundStyle)
          ? new Fill({
            color: labelStyleConfig.backgroundColor
          })
          : undefined,
        // @ts-ignore: Uncomment when OL releases ^6.13.0 which includes `justify`
        justify: 'left' // We always have text alignment within the label to the left
      }),
      zIndex
    });

    return labelStyle;
  }

}

function convertAttributeStyles (labelStyles: LabelStyleSourceData[]): AttributeStyle[][] {
  return labelStyles.map(labelStyle => {
    return labelStyle.attributeStyles.map(attributeStyle => {
      const cssFont = createCssFontText(attributeStyle.font);
      return {
        name: attributeStyle.name,
        font: createCssFontShorthand(cssFont)
      };
    });
  });
}

function createCssFontText (font: FontSourceData) {
  const p = document.createElement('p');
  p.style.fontFamily = fonts[font.fontFamily] ?? fonts[FALLBACK_FONT_FAMILY];
  p.style.fontSize = toCssFontSize(font.fontSize);
  p.style.fontStyle = font.fontStyle;
  p.style.fontWeight = font.fontWeight;
  p.style.textDecorationColor = font.textDecorationColor;
  p.style.textDecorationLine = font.textDecorationLine;
  p.style.textDecorationThickness = font.textDecorationThickness;

  return p.style;
}

function createCssFontShorthand (font: CSSStyleDeclaration) {
  return `${font.fontStyle} ${font.fontWeight} ${font.fontSize}/${LINE_HEIGHT_SCALE_FACTOR} ${font.fontFamily}`;
}

function isHalo (backgroundStyle: string) {
  return backgroundStyle === BACKGROUND_STYLE_HALO;
}
